import { isPlatformServer, PlatformLocation } from '@angular/common';
import { inject, Injectable, Injector, isDevMode, PLATFORM_ID, signal, TransferState } from '@angular/core';
import { APP_CONFIG as LOCAL_APP_CONFIG } from '@core-app/app.config';
import { resolveRequestHostname } from '@core-app/interceptors/forwarded-host.interceptor';
import { BOOTSTRAP_STATE_KEY, BootstrapResponse, BootstrapSnapshot } from '@core-app/tokens';
import { ChatService } from '@gallaghercp/core-chat';
import {
    AnalyticsConfiguration,
    AnalyticsService,
    APIInterceptor,
    APP_CONFIG as CORE_APP_CONFIG,
    CacheService,
    CONFIG,
    SettingsService,
    updateAppConfig,
    ZoneService,
} from '@gallaghercp/core-frontend';
import { Store } from '@ngrx/store';
import { catchError, defer, EMPTY, from, map, Observable, of, retry, switchMap, take, tap, throwError } from 'rxjs';

const EXCLUDED_CONFIG_KEYS: ReadonlySet<string> = new Set([
    'api',
    'apiHash',
    'apiSSO',
    'commitSHA',
    'frontend',
    'provider',
    'authHeaders',
    'modernised',
    'demoMode',
    'debug',
    'featureFlags',
]);

const localConfig = LOCAL_APP_CONFIG as unknown as Partial<CONFIG>;

@Injectable({
    providedIn: 'root',
})
export class BootstrapService {
    public readonly failed = signal(false);

    private readonly settings = inject(SettingsService);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly chat = inject(ChatService);
    private readonly analytics = inject(AnalyticsService);
    private readonly apiInterceptor = inject(APIInterceptor);
    private readonly cache = inject(CacheService);
    private readonly transferState = inject(TransferState);
    private readonly platformLocation = inject(PlatformLocation);
    private readonly store = inject(Store);
    private readonly injector = inject(Injector);

    public markFailed(): void {
        this.failed.set(true);
    }

    public initialise(): Observable<void> {
        if (localConfig.modernised !== true) {
            return this.initialiseLegacy();
        }

        const isServer = isPlatformServer(this.platformId);
        const requestHostname = isServer ? resolveRequestHostname(this.platformLocation) : null;
        const config = this.prepareInitialConfig(isServer, requestHostname);

        const snapshot = isServer ? null : this.transferState.get(BOOTSTRAP_STATE_KEY, null);

        const bootstrap$ = snapshot
            ? this.hydrateFromSnapshot(snapshot, config, isServer)
            : this.bootstrapFromRemote(config, isServer, requestHostname);

        // Legacy initialisation (zonal routes, settings, zones) is still required once the
        // modernised bootstrap has resolved the tenant configuration.
        return bootstrap$.pipe(switchMap(() => this.initialiseLegacy()));
    }

    private initialiseLegacy(): Observable<void> {
        // Zonal routes come from app.config `zoneRouting` (array of { id, route }).
        ZoneService.zonalRoutes = ((localConfig['zoneRouting'] ?? []) as { route?: string }[])
            .map(zone => zone?.route)
            .filter((route): route is string => !!route);

        const config: CONFIG = { ...(localConfig as CONFIG) };

        if (!isPlatformServer(this.platformId) && localConfig.enableSSR) {
            config.api = '/api/2.0';
        }

        const zones = this.injector.get(ZoneService);
        return from(this.settings.init(config)).pipe(
            switchMap(() => zones.retrieveEnabledZones()),
            tap(() => {
                this.chat.setApiInterceptor(this.apiInterceptor);
            }),
            map(() => void 0),
        );
    }

    private prepareInitialConfig(isServer: boolean, requestHostname: string | null): CONFIG {
        const config = this.cloneConfig(localConfig as CONFIG);

        if (!isServer) {
            config.api = '/api/2.0';
        } else if (requestHostname) {
            // `apiHash` stands in for the public origin when signing requests, and there is no
            // `window.location` on the server to derive it from.
            config.apiHash = `https://${requestHostname}/api/1.0`;
        }

        return config;
    }

    private hydrateFromSnapshot(snapshot: BootstrapSnapshot, config: CONFIG, isServer: boolean): Observable<void> {
        return of(void 0).pipe(
            tap(() => {
                this.transferState.remove(BOOTSTRAP_STATE_KEY);
                this.applyBootstrapSnapshot(snapshot, config);
                this.connectIntegrations();
            }),
            tap(() => {
                this.executeInit(config, isServer)
                    .pipe(
                        catchError((err: unknown) => {
                            console.error('Background bootstrap refresh failed:', err);
                            return EMPTY;
                        }),
                    )
                    .subscribe();
            }),
        );
    }

    private bootstrapFromRemote(config: CONFIG, isServer: boolean, requestHostname: string | null): Observable<void> {
        return this.executeInit(config, isServer).pipe(
            switchMap(() => this.resolveTenant(isServer, requestHostname)),
            tap(bootstrap => {
                this.applyResolvedBootstrap(bootstrap);

                if (isServer) {
                    if (requestHostname) {
                        CORE_APP_CONFIG.apiHash = `https://${requestHostname}/api/1.0`;
                    }

                    this.transferState.set(BOOTSTRAP_STATE_KEY, this.captureBootstrapSnapshot());
                }

                this.connectIntegrations();
            }),
            map(() => void 0),
            catchError((err: unknown) => {
                console.error('Bootstrap failed:', err);

                if (isServer && !isDevMode()) {
                    return throwError(() => err);
                }

                this.markFailed();
                return EMPTY;
            }),
        );
    }

    private connectIntegrations(): void {
        this.analytics.initialise({
            gtm: { id: CORE_APP_CONFIG.gtm, consent: 3 },
        } as AnalyticsConfiguration);

        this.chat.setApiInterceptor(this.apiInterceptor);
    }

    private executeInit(config: CONFIG, isServer: boolean): Observable<void> {
        return from(this.settings.init(config)).pipe(
            retry({ count: isServer ? 1 : 4, delay: 1500 }),
            map(() => void 0),
        );
    }

    private resolveTenant(isServer: boolean, requestHostname: string | null): Observable<BootstrapResponse> {
        return defer(() => this.cache.getOneThroughCache('bootstrap', null).pipe(take(1))).pipe(
            map((response: unknown) => {
                if (this.isBootstrapResponse(response)) {
                    return response;
                }

                const origin = isServer ? `forwarded host "${requestHostname}"` : 'the browser origin';
                throw new Error(`Bootstrap failed to resolve a tenant for ${origin}`);
            }),
        );
    }

    private isBootstrapResponse(response: unknown): response is BootstrapResponse {
        return (
            typeof response === 'object' &&
            response !== null &&
            'tenantId' in response &&
            typeof (response as { tenantId: unknown }).tenantId === 'string' &&
            (response as { tenantId: string }).tenantId.length > 0
        );
    }

    private applyResolvedBootstrap(bootstrap: BootstrapResponse): void {
        const sourceConfig = bootstrap.appConfig;

        if (sourceConfig) {
            this.mergeConfig(CORE_APP_CONFIG as Record<string, unknown>, sourceConfig as Record<string, unknown>);
            this.mergeConfig(localConfig as Record<string, unknown>, sourceConfig as Record<string, unknown>);
            this.store.dispatch(updateAppConfig(localConfig));
        }
    }

    private mergeConfig<T extends Record<string, unknown>>(target: T, source: Record<string, unknown>): void {
        for (const [key, value] of Object.entries(source)) {
            if (EXCLUDED_CONFIG_KEYS.has(key) || value === undefined || value === null) {
                continue;
            }

            if (this.isPlainObject(value)) {
                const existing = this.isPlainObject(target[key]) ? (target[key] as Record<string, unknown>) : {};
                (target as Record<string, unknown>)[key] = this.deepMerge(existing, value);
            } else if (Array.isArray(value)) {
                (target as Record<string, unknown>)[key] = [...value];
            } else {
                (target as Record<string, unknown>)[key] = value;
            }
        }
    }

    private deepMerge(base: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
        const result: Record<string, unknown> = { ...base };
        this.mergeConfig(result, source);
        return result;
    }

    private isPlainObject(item: unknown): item is Record<string, unknown> {
        return typeof item === 'object' && item !== null && !Array.isArray(item);
    }

    private cloneConfig<T>(source: T): T {
        if (typeof structuredClone === 'function') {
            return structuredClone(source);
        }
        return JSON.parse(JSON.stringify(source)) as T;
    }

    private captureBootstrapSnapshot(): BootstrapSnapshot {
        const snapshot: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(CORE_APP_CONFIG)) {
            if (!EXCLUDED_CONFIG_KEYS.has(key) && value !== undefined) {
                snapshot[key] = value;
            }
        }

        if (CORE_APP_CONFIG.apiHash) {
            snapshot['apiHash'] = CORE_APP_CONFIG.apiHash;
        }

        return snapshot as BootstrapSnapshot;
    }

    private applyBootstrapSnapshot(snapshot: BootstrapSnapshot, config: CONFIG): void {
        if (snapshot.apiHash) {
            CORE_APP_CONFIG.apiHash = snapshot.apiHash;
            localConfig.apiHash = snapshot.apiHash;
            config.apiHash = snapshot.apiHash;
        }

        this.mergeConfig(CORE_APP_CONFIG as Record<string, unknown>, snapshot as Record<string, unknown>);
        this.mergeConfig(localConfig as Record<string, unknown>, snapshot as Record<string, unknown>);
        this.mergeConfig(config as Record<string, unknown>, snapshot as Record<string, unknown>);
        this.store.dispatch(updateAppConfig(localConfig));
    }
}
